import { ClosedReason } from 'eve-account-client';
import React from 'react';
import { DropdownControl, TextAreaControl } from '@ver-uds/uswds-react';
import { useFormContext } from 'react-hook-form';
import Section from './Section';

export type TerminationReasonSelectionProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  closedReasons?: ClosedReason[];
  noMarginBottom?: boolean;
};

type FormValues = {
  closedReasonCode: string;
  closedReasonText: string;
  childClosedReasonCode: string;
};

const TerminationReasonSelection = ({
  closedReasons,
  noMarginBottom,
}: TerminationReasonSelectionProps): JSX.Element => {
  // the logic below is for re-mapping the flat JSON array into a map that consists of the
  // groupName and the reasons as the key's values
  const mappedClosedReasons = new Map<string, ClosedReason[]>();

  const OTHER_REASON_CODE = 'OTHER';
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, watch, errors } = useFormContext<FormValues>();
  const { closedReasonCode } = watch();

  // eslint-disable-next-line no-unused-expressions
  closedReasons?.forEach((reason) => {
    if (reason.groupName && reason.code !== OTHER_REASON_CODE) {
      const children = closedReasons.filter((child) => reason.groupName === child.groupName);
      mappedClosedReasons.set(reason.groupName, children);
    } else {
      mappedClosedReasons.set(reason.displayName, [reason]);
    }
  });

  const mappedChildClosedReasons = mappedClosedReasons.get(closedReasonCode) || [];

  return (
    <Section className={`desktop:grid-col-7${noMarginBottom ? ' margin-bottom-0' : ''}`}>
      <DropdownControl
        id="dropdown-required"
        name="closedReasonCode"
        label="What is the primary reason for requesting account termination?"
        required
        ref={register({
          required: {
            value: true,
            message: 'A termination request reason is required.',
          },
        })}
        errorId={errors.closedReasonCode && 'termination-reason-error'}
        errorMessage={errors.closedReasonCode?.message}
      >
        <option value="">- Select -</option>

        {Array.from(mappedClosedReasons.keys())?.map((groupName) => {
          const childrenCount = mappedClosedReasons.get(groupName)?.length || 0;
          if (childrenCount > 1) {
            return (
              <option key={groupName} value={groupName}>
                {groupName}
              </option>
            );
          }
          return (
            <option
              key={groupName}
              value={mappedClosedReasons.get(groupName)?.find((c) => c.displayName === groupName)?.code}
            >
              {groupName}
            </option>
          );
        })}
      </DropdownControl>
      {mappedChildClosedReasons.length > 0 && (
        <DropdownControl
          id="dropdown-required-child"
          name="childClosedReasonCode"
          label="Please specify"
          required
          ref={register({
            required: {
              value: true,
              message: 'A selection is required.',
            },
          })}
          errorId={errors.childClosedReasonCode && 'termination-reason-child-error'}
          errorMessage={errors.childClosedReasonCode?.message}
        >
          <option value="">- Select -</option>

          {mappedClosedReasons.get(closedReasonCode)?.map((reason) => (
            <option key={reason.displayName} value={reason.code}>
              {reason.displayName}
            </option>
          ))}
        </DropdownControl>
      )}

      {closedReasonCode === OTHER_REASON_CODE && (
        <TextAreaControl
          id="termination-reason-description"
          label="Please specify"
          name="closedReasonText"
          ref={register({
            required: {
              value: true,
              message: 'A description is required for the termination request reason.',
            },
            maxLength: {
              value: 250,
              message: 'Please enter no more than 250 characters.',
            },
          })}
          required
          characterCountMax={250}
          errorMessage={errors.closedReasonText?.message}
        />
      )}
    </Section>
  );
};

export default TerminationReasonSelection;
